import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { parseISO, format } from "date-fns";
import { Edit, Trash2, EyeIcon as Eye } from 'lucide-react';
import { ChevronDown, ChevronUp } from "lucide-react";
import DeckService from "../../services/decks.service";
import { isValidURL } from "@/utils/validation";
import { baseURL } from "@/config/config";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";

interface user {
    _id: string;
    full_name: string;
    email_address: string;
    phone_number: string;
    role: string;
    status: string;
    createdAt: string;
}

interface category {
    _id: string;
    title: string;
}

interface Deck {
    _id: string;
    title: string;
    description: string;
    tags?: string[];
    type: string;
    likes: number;
    completion_count: number;
    average_time_spent: number;
    view_count: number;
    active_usage_count: number;
    retention_rate: number;
    search_appearances: number;
    click_count: number;
    conversion_rate: number;
    createdAt: string;
    user: user[];
    flashcard_count: number;
    category: category[];
    image: string;
    isPro?: boolean | null;
    isPublic?: boolean | null;
}

interface DeckTableProps {
    decks: Deck[];
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    isLoading: boolean;
}

const LoadingSkeleton = () => (
    <div className="animate-pulse">
        <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
                <div key={index} className="flex items-center space-x-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-1/4 dark:bg-gray-700"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4 dark:bg-gray-700"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/6 dark:bg-gray-700"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/6 dark:bg-gray-700"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/6 dark:bg-gray-700"></div>
                </div>
            ))}
        </div>
    </div>
);

export default function DeckTable({ decks, currentPage, totalPages, onPageChange, isLoading }: DeckTableProps) {
    const [selectedDeck, setSelectedDeck] = useState<Deck | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mediaSrc, setMediaSrc] = useState<any>("");
    const [expandedTags, setExpandedTags] = useState<Record<string, boolean>>({});
    const [publicStatusMap, setPublicStatusMap] = useState<Record<string, boolean>>({});
    const [updatingPublicMap, setUpdatingPublicMap] = useState<Record<string, boolean>>({});
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const nextMap: Record<string, boolean> = {};
        decks.forEach((deck) => {
            nextMap[deck._id] = deck.isPublic ?? false;
        });
        setPublicStatusMap(nextMap);
    }, [decks]);

    const toggleTags = (deckId: string) => {
        setExpandedTags((prev) => ({
            ...prev,
            [deckId]: !prev[deckId],
        }));
    };

    const handleOpenMedia = async (src: string) => {
        setMediaSrc(isValidURL(src) ? src : baseURL + src);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        setIsLoadingButton(true);
        if (!selectedDeck) {
            setIsLoadingButton(false);
            return;
        }
        try {
            const rs = await DeckService.deleteDeck(selectedDeck._id);
            if (rs?.data?.status === 200) {
                setTimeout(() => {
                    // onPageChange(1);
                    window.location.reload();
                }, 100);
            }
        } catch (error) {
            console.error("Error deleting deck:", error);
            setIsLoadingButton(false);
            setIsDialogOpen(false);
            setSelectedDeck(null);
        }
    };

    const handleTogglePublic = async (deck: Deck) => {
        const currentValue = publicStatusMap[deck._id] ?? (deck.isPublic ?? false);
        const nextValue = !currentValue;
        const toastId = toast.loading(t("Updating deck visibility..."));

        setPublicStatusMap((prev) => ({
            ...prev,
            [deck._id]: nextValue,
        }));
        setUpdatingPublicMap((prev) => ({
            ...prev,
            [deck._id]: true,
        }));

        try {
            const fd = new FormData();
            fd.append("isPublic", String(nextValue));
            fd.append("_id", deck._id);

            const response = await DeckService.updateDeckWithObjecId(deck._id, fd as any);
            if (response?.data?.status !== 200) {
                throw new Error("Update isPublic failed");
            }
            toast.update(toastId, {
                render: nextValue ? t("Deck has been public") : t("Deck has been private"),
                type: "success",
                isLoading: false,
                autoClose: 2000,
                closeOnClick: true,
            });
        } catch (error) {
            setPublicStatusMap((prev) => ({
                ...prev,
                [deck._id]: currentValue,
            }));
            toast.update(toastId, {
                render: t("Failed to update Is Public. Please try again."),
                type: "error",
                isLoading: false,
                autoClose: 2500,
                closeOnClick: true,
            });
            console.error("Error updating isPublic:", error);
        } finally {
            setUpdatingPublicMap((prev) => ({
                ...prev,
                [deck._id]: false,
            }));
        }
    };

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <div className="min-w-[1102px]">
                    <Table>
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Title")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Image")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Category")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Tags")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Number of Flashcards")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Created By")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Created At")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Is Pro")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Is Public")}
                                </TableCell>

                                <TableCell isHeader className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start">
                                    {t("Actions")}
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {isLoading ? (
                                <LoadingSkeleton />
                            ) : decks.map((deck) => (
                                <TableRow key={deck._id}>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {deck.title}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400 w-1/9">
                                        <div className="ms-1">
                                            {deck.image ? (
                                                <button
                                                    onClick={() => handleOpenMedia(deck.image)}
                                                    className="text-gray-700 dark:text-gray-400 hover:text-blue-700"
                                                >
                                                    <img src={deck.image} alt="Preview" className="max-h-96 mx-auto rounded-md" style={{ width: '100px' }} />
                                                </button>
                                            ) : "N/A"}
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {(deck.category && deck.category.length !== 0) ? deck.category[0]?.title : "N/A"}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 w-[200px] max-w-[200px] text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {deck.tags && deck.tags.length !== 0 ? (
                                            <>
                                                {expandedTags[deck._id]
                                                    ? deck.tags.map((tag: string, idx: number) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm mr-1 mb-1"
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))
                                                    : deck.tags.slice(0, 2).map((tag: string, idx: number) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm mr-1 mb-1"
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))}

                                                {deck.tags.length > 2 && (
                                                    <button
                                                        onClick={() => toggleTags(deck._id)}
                                                        className="ml-2 inline-flex items-center text-blue-600 text-sm"
                                                    >
                                                        {expandedTags[deck._id] ? (
                                                            <ChevronUp size={16} />
                                                        ) : (
                                                            <ChevronDown size={16} />
                                                        )}
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            "No tags"
                                        )}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {deck.flashcard_count || 0}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {deck?.user[0]?.full_name || "Admin"}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {format(parseISO(deck.createdAt), "MM/dd/yyyy hh:mm a")}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                (deck.isPro ?? false)
                                                    ? "bg-emerald-100 text-emerald-700"
                                                    : "bg-gray-100 text-gray-700"
                                            }`}
                                        >
                                            {(deck.isPro ?? false) ? "Pro" : "Free"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {(() => {
                                            const isPublicValue = publicStatusMap[deck._id] ?? (deck.isPublic ?? false);
                                            const isUpdating = updatingPublicMap[deck._id];
                                            return (
                                                <div
                                                    role="group"
                                                    aria-label={t("Toggle Is Public")}
                                                    className={`relative inline-flex items-center rounded-full bg-gray-100 p-1 dark:bg-white/[0.06] ${
                                                        isUpdating ? "opacity-60 cursor-not-allowed" : ""
                                                    }`}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => isPublicValue && !isUpdating && handleTogglePublic(deck)}
                                                        disabled={isUpdating}
                                                        aria-pressed={!isPublicValue}
                                                        className={`relative z-10 rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
                                                            !isPublicValue
                                                                ? "bg-yellow-400 text-white shadow-sm dark:bg-yellow-400 dark:text-white"
                                                                : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                                        }`}
                                                    >
                                                        {t("Private")}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => !isPublicValue && !isUpdating && handleTogglePublic(deck)}
                                                        disabled={isUpdating}
                                                        aria-pressed={isPublicValue}
                                                        className={`relative z-10 rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
                                                            isPublicValue
                                                                ? "bg-yellow-400 text-white shadow-sm dark:bg-yellow-400 dark:text-white"
                                                                : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                                        }`}
                                                    >
                                                        {t("Public")}
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <button
                                            onClick={() => {
                                                navigate(`/list-flashcards/${deck._id}`, {
                                                    state: {
                                                        returnTo: `${location.pathname}${location.search}`
                                                    }
                                                });
                                            }}
                                            className="p-2 text-yellow-500 hover:bg-yellow-100 rounded-md"
                                        >
                                            <Eye className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => {
                                                navigate(`/edit-deck/${deck._id}`, {
                                                    state: {
                                                        returnTo: `${location.pathname}${location.search}`
                                                    }
                                                });
                                            }}
                                            className="p-2 text-blue-500 hover:bg-blue-100 rounded-md"
                                        >
                                            <Edit className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedDeck(deck);
                                                setIsDialogOpen(true);
                                            }}
                                            className="p-2 text-red-500 hover:bg-red-100 rounded-md"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                        {
                                            isDialogOpen && (
                                                <>
                                                    {/* <div className="fixed inset-0 backdrop-blur-md shadow-2xl z-40" /> */}
                                                    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-20">
                                                        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
                                                            <h2 className="text-xl font-semibold mb-4">{t('Confirm Deletion')}</h2>
                                                            <p className="text-gray-600 mb-6">{t('Are you sure you want to delete this deck? This action is irreversible.')}</p>
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        setIsDialogOpen(false);
                                                                        setSelectedDeck(null);
                                                                    }}
                                                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                                                >
                                                                    {t('Cancel')}
                                                                </button>
                                                                <button
                                                                    onClick={handleDelete}
                                                                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                                                                >
                                                                    {isLoadingButton ? (
                                                                        <div className="flex items-center justify-center gap-2">
                                                                            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                                            </svg>
                                                                            {t('Deleting')}...
                                                                        </div>
                                                                    ) : (
                                                                        <> {t('Delete')}</>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )
                                        }

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
            {decks.length !== 0 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 dark:bg-white/[0.03] dark:border-white/[0.05]">
                    <div className="flex flex-1 justify-center">
                        <nav className="relative z-0 inline-flex -space-x-px rounded-md" aria-label="Pagination">
                            <button
                                onClick={() => onPageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Previous</span>
                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    onClick={() => onPageChange(page)}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${currentPage === page
                                        ? "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                        : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                onClick={() => onPageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Next</span>
                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </nav>
                    </div>
                </div>
            )}
            {isModalOpen && (
                <>
                    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl"
                            >
                                &times;
                            </button>

                            <h2 className="text-lg font-semibold mb-4 text-center">
                                Image Preview
                            </h2>

                            <div className="text-center">
                                <img src={mediaSrc} alt="Preview" className="max-h-96 mx-auto rounded-md" />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
